import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, Switch, DatePicker } from "antd";
import { useCreate, useUpdate } from "../../../../hooks";
import { masterDataService } from "../../../../api/enhancedApi";
import { CARGO_AIRCRAFT_TYPES, CARGO_AIRCRAFT_TYPE_OPTIONS } from "../../../../constants/aircraftTypes";
import dayjs from "dayjs";

const { TextArea } = Input;

/**
 * 飞机表单组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示表单对话框
 * @param {string} props.mode - 表单模式，'add'或'edit'
 * @param {Object} props.initialValues - 初始值，编辑模式下必传
 * @param {Function} props.onCancel - 取消回调函数
 * @param {Function} props.onSuccess - 提交成功回调函数
 */
const AircraftForm = ({ visible, mode, initialValues, onCancel, onSuccess }) => {
    const [form] = Form.useForm();

    // 与销售合同航线同一份机型。编辑旧飞机时，库里已有、清单里没有的机型仍显示，避免一打开就把原值弄丢。
    const currentType = initialValues?.aircraft_type;
    const aircraftTypeOptions = useMemo(() => {
        if (currentType && !CARGO_AIRCRAFT_TYPES.includes(currentType)) {
            return [...CARGO_AIRCRAFT_TYPE_OPTIONS, { label: currentType, value: currentType }];
        }
        return CARGO_AIRCRAFT_TYPE_OPTIONS;
    }, [currentType]);

    // 创建飞机的Hook
    const createMutation = useCreate("aircraft", masterDataService.aircraft.createAircraft, {
        onSuccess: () => {
            onSuccess();
            form.resetFields();
        },
    });

    // 更新飞机的Hook
    const updateMutation = useUpdate("aircraft", masterDataService.aircraft.updateAircraft, {
        onSuccess: () => {
            onSuccess();
        },
    });

    // 当initialValues变化时重置表单
    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue({
                registration: initialValues.registration,
                aircraft_type: initialValues.aircraft_type,
                serial_number: initialValues.serial_number,
                flight_number_offset: initialValues.flight_number_offset || 0,
                mtow: initialValues.mtow,
                range_km: initialValues.range_km,
                status: initialValues.status === "ACTIVE",
                availability_start: initialValues.availability_start ? dayjs(initialValues.availability_start) : null,
                remarks: initialValues.remarks,
            });
        } else if (visible) {
            form.resetFields();
            // 设置默认值
            form.setFieldsValue({
                status: true,
                flight_number_offset: 0,
            });
        }
    }, [visible, initialValues, form]);

    // 表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 处理表单数据，转换为API需要的格式
            const apiData = {
                ...values,
                status: values.status ? "ACTIVE" : "INACTIVE",
                availability_start: values.availability_start ? values.availability_start.format("YYYY-MM-DD") : null,
            };

            if (mode === "add") {
                createMutation.mutate(apiData);
            } else {
                updateMutation.mutate({
                    id: initialValues.id,
                    data: apiData,
                });
            }
        } catch (error) {
            console.error("表单验证错误:", error);
        }
    };

    // 表单标题
    const title = mode === "add" ? "添加飞机" : "编辑飞机";

    // 是否处于提交中状态
    const loading = createMutation.isLoading || updateMutation.isLoading;

    return (
        <Modal title={title} open={visible} onCancel={onCancel} onOk={handleSubmit} confirmLoading={loading} maskClosable={false} destroyOnHidden>
            <Form form={form} layout="horizontal" labelAlign="left">
                <div className="form-inline-group">
                    <Form.Item name="registration" label="注册号" rules={[{ required: true, message: "请输入飞机注册号" }]}>
                        <Input placeholder="请输入飞机注册号" maxLength={10} />
                    </Form.Item>

                    <Form.Item name="aircraft_type" label="机型" rules={[{ message: "请选择机型" }]}>
                        <Select placeholder="请选择机型" options={aircraftTypeOptions} />
                    </Form.Item>

                    <Form.Item name="serial_number" label="序列号" rules={[{ message: "请输入序列号" }]}>
                        <Input placeholder="请输入序列号" />
                    </Form.Item>

                    <Form.Item name="mtow" label="最大起飞重量(吨)" rules={[{ type: "number", min: 0, message: "重量必须大于0" }]}>
                        <InputNumber placeholder="请输入最大起飞重量" style={{ width: "100%" }} min={0} step={0.1} precision={1} />
                    </Form.Item>

                    <Form.Item name="range_km" label="航程(公里)" rules={[{ type: "number", min: 0, message: "航程必须大于0" }]}>
                        <InputNumber placeholder="请输入航程" style={{ width: "100%" }} min={0} step={100} precision={0} />
                    </Form.Item>

                    <Form.Item name="flight_number_offset" label="航班号偏移量" tooltip="用于和航段编号相加计算航班号" rules={[{ type: "number", required: true, message: "请输入航班号偏移量" }]}>
                        <InputNumber placeholder="请输入航班号偏移量" style={{ width: "100%" }} min={0} precision={0} />
                    </Form.Item>

                    <Form.Item name="availability_start" label="可用开始日期">
                        <DatePicker style={{ width: "100%" }} placeholder="请选择可用开始日期" format="YYYY-MM-DD" />
                    </Form.Item>
                </div>

                <Form.Item name="status" label="状态" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="停用" />
                </Form.Item>

                <Form.Item name="remarks" label="备注">
                    <TextArea rows={4} placeholder="请输入备注信息" maxLength={500} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AircraftForm;
